import { useMutation, useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import { LogLinearScaleSelector, type AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { AreaBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import IssuersSelect from "components/charts/modern/overlay/IssuersSelect/IssuersSelect";
import SelectedIssuersStack from "components/charts/modern/overlay/IssuersSelect/SelectedIssuersStack";
import { SELECTED_ISSUERS_ATOM } from "components/charts/modern/overlay/IssuersSelect/atoms";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import { getLabelForBookmarkTooltip } from "components/company/valuation/IndicatorSnapshot";
import valuationIndicatorFormatValue from "components/company/valuation/utils/valuationIndicatorFormat";
import { verifyIndicatorIsValuationRatio } from "components/company/valuation/utils/verifyIndicatorIsValuationRatios";
import Avatar from "components/core/Avatar";
import SadFaceIcon from "components/icons/SadFaceIcon";
import { Button } from "components/ui/Button";
import ExpandableParagraph from "components/ui/ExpandableParagraph";
import { Separator } from "components/ui/Separator";
import { SheetDescription, SheetHeader, SheetTitle } from "components/ui/Sheet";
import { SimpleTextTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import compareDesc from "date-fns/compareDesc";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import type { SeriesAreaOptions, SeriesColumnOptions, SeriesLineOptions } from "highcharts";
import { useAtomValue } from "jotai";
import ToggleBookmarkForIndicatorMutation from "lib/graphql/mutations/settings/ToggleBookmarkForIndicatorMutation";
import type {
  IToggleBookmarkForIndicatorMutation,
  IToggleBookmarkForIndicatorMutationVariables,
} from "lib/graphql/mutations/settings/__generated__/ToggleBookmarkForIndicatorMutation.generated";
import useSelfie from "lib/hooks/useSelfie";
import ValuationIndicatorHistoryForTickerSymbolsQuery from "lib/queries/ValuationIndicatorHistoryForTickerSymbolsQuery";
import type {
  IValuationIndicatorHistoryForTickerSymbolsQuery,
  IValuationIndicatorHistoryForTickerSymbolsQueryVariables,
} from "lib/queries/__generated__/ValuationIndicatorHistoryForTickerSymbolsQuery.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import { BookmarkIcon, GraduationCapIcon } from "lucide-react";
import { useRouter } from "next/router";
import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { IntlShape } from "react-intl";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import type { ISecurityCountry, IValuationIndicatorVerbiageLanguage } from "types/graphqlTypes";
import { IIssuerListingCountry, IValuationIndicator, IValuationPeriod } from "types/graphqlTypes";

interface IValuationHistoryProps {
  verbiageLanguage: IValuationIndicatorVerbiageLanguage;
  securityCountry: ISecurityCountry;
  tickerSymbol: string;
  indicator: IValuationIndicator;
}

export default function ValuationHistory({
  securityCountry,
  tickerSymbol,
  verbiageLanguage,
  indicator,
}: IValuationHistoryProps) {
  const [plotType, setPlotType] = useState<"area" | "column">("area");
  const [axisScaleType, setAxisScaleType] = useState<AxisScaleType>("linear");
  const [filterPeriod, setFilterPeriod] = useState<IValuationPeriod>(IValuationPeriod.YearEnd_10Fiscalyear);

  const intl = useIntl();
  const selfie = useSelfie();
  const issuer = useRootIssuer();

  const { locale } = useRouter();
  const isUSALocale = locale?.startsWith("en") || false;
  const selectedIssuers = useAtomValue(SELECTED_ISSUERS_ATOM);
  const tickerSymbolsForSeries = [tickerSymbol].concat(selectedIssuers.map((node) => node.tickerSymbol.toUpperCase()));

  const { data, previousData, loading } = useQuery<
    IValuationIndicatorHistoryForTickerSymbolsQuery,
    IValuationIndicatorHistoryForTickerSymbolsQueryVariables
  >(ValuationIndicatorHistoryForTickerSymbolsQuery, {
    variables: {
      tickerSymbols: tickerSymbolsForSeries,
      securityCountry,
      indicator,
      verbiageLanguage,
      period: filterPeriod,
    },
  });

  const [toggleBookmarkForIndicator, { loading: toggleBookmarkLoading, error }] = useMutation<
    IToggleBookmarkForIndicatorMutation,
    IToggleBookmarkForIndicatorMutationVariables
  >(ToggleBookmarkForIndicatorMutation, {
    variables: {
      indicator,
    },
  });
  const hasError = !!error;

  const valuationIndicatorHistoryForTickerSymbols =
    (loading && !data
      ? previousData?.valuationIndicatorHistoryForTickerSymbols
      : data?.valuationIndicatorHistoryForTickerSymbols) || [];

  if (loading && valuationIndicatorHistoryForTickerSymbols.length === 0) {
    return (
      <div className="contents">
        <SheetHeader className="flex animate-pulse items-start justify-between pt-10">
          <div className="flex flex-col space-y-2">
            <SheetTitle>
              <span className="h-10 w-24 rounded-xl bg-slate-200 md:w-32" />
            </SheetTitle>
            <div className="h-6 w-36 rounded-md bg-slate-200 md:w-64" />
          </div>

          <div className="flex flex-col items-end space-y-2">
            <SheetDescription>
              <span className="flex h-16 w-24 rounded-lg bg-slate-200 p-1 md:w-44" />
            </SheetDescription>

            <div className="flex h-2.5 w-12 items-center space-x-2 rounded-md bg-slate-200 md:w-28" />
          </div>
        </SheetHeader>

        <div className="mt-4 h-6 w-24 animate-pulse rounded-lg bg-slate-200" />

        <div className="mt-4 animate-pulse space-y-2">
          <div className="h-2.5 w-full rounded bg-slate-200" />
          <div className="h-2.5 w-full rounded bg-slate-200" />
          <div className="h-2.5 w-1/2 rounded bg-slate-200" />
        </div>

        <Separator className="mb-4 mt-7 bg-gray-100" />

        <div className="flex animate-pulse justify-between">
          <div className="flex space-x-4">
            <div className="h-10 w-20 rounded-xl bg-slate-200" />
            <div className="h-10 w-28 rounded-md bg-slate-200" />
          </div>

          <div className="h-10 w-10 rounded-full bg-slate-200" />
        </div>

        <div className="mt-4 h-60 w-full animate-pulse rounded-lg bg-slate-200" />
      </div>
    );
  }

  if (valuationIndicatorHistoryForTickerSymbols.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 font-aeonik">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          {isUSALocale ? <p>{`Couldn't find any data.`}</p> : <p>Nenhum dado foi encontrado.</p>}
        </div>
      </div>
    );
  }

  const valuationData = valuationIndicatorHistoryForTickerSymbols.find(
    (node) => node.tickerSymbol === tickerSymbol.toUpperCase(),
  );

  if (!valuationData || valuationData.nodes.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 font-aeonik">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          {isUSALocale ? <p>{`Couldn't find any data.`}</p> : <p>Nenhum dado foi encontrado.</p>}
        </div>
      </div>
    );
  }

  const [mostRecent] = [...valuationData.nodes].sort((a, b) =>
    compareDesc(parseDateYmd(a.asOfDate), parseDateYmd(b.asOfDate)),
  );
  const isAdjusted = String(mostRecent.indicator).includes("NORMALIZED");
  const isAnnual = verifyIsAnnual(filterPeriod, indicator);
  const isBookmarked = selfie ? selfie.settings.bookmarkedIndicators.includes(mostRecent.indicator) : false;

  return (
    <div className="flex flex-col space-y-6 pt-10">
      <div className="flex items-start justify-between">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center space-x-2">
            <SheetTitle className="m-0 text-3xl font-black text-slate-700 sm:text-4xl">
              {mostRecent.verbiage.abbreviation}
            </SheetTitle>
            {selfie !== null && (
              <div className="flex space-x-2">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger
                      className={classNames(
                        "inline-flex h-8 w-8 items-center justify-center rounded-lg",
                        "bg-orange-50 text-orange-400",
                        {
                          "bg-red-50 text-red-400": hasError,
                          "pointer-events-none": toggleBookmarkLoading,
                        },
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleBookmarkForIndicator();
                      }}
                    >
                      {isBookmarked ? (
                        <BookmarkIcon fill="currentColor" className="fill-orange-400" />
                      ) : (
                        <BookmarkIcon />
                      )}
                    </TooltipTrigger>

                    <TooltipContent className="rounded-lg bg-gray-600 p-2 font-aeonik text-xs" side="top">
                      <span className="font-bold tracking-wider text-white">
                        {getLabelForBookmarkTooltip(isBookmarked, hasError, verbiageLanguage)}
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>

          <h3 className="m-0 text-base font-medium -tracking-wide text-gray-400 sm:text-lg">
            {mostRecent.verbiage.formula}
          </h3>
        </div>

        <div className="flex flex-col items-end space-y-2">
          <div className="flex rounded-lg bg-violet-50 p-1">
            <h4 className="flex rounded border border-violet-300 bg-white px-11 py-3.5 text-2xl font-bold text-violet-700 sm:px-14 sm:py-4 sm:text-3xl">
              {mostRecent.formattedValue}
            </h4>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-[9px] font-medium italic text-gray-400">
              {intl.formatDate(parseDateYmd(mostRecent.asOfDate), {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              })}
            </span>

            <div
              className={classNames("rounded-full bg-emerald-50 p-1.5", {
                "text-purple-50": isAdjusted,
                "text-red-50": mostRecent.value < 0,
                "text-gray-50": mostRecent.value === 0,
              })}
            >
              <div
                className={classNames("h-2 w-2 rounded-full bg-emerald-600", {
                  "bg-purple-600": isAdjusted,
                  "bg-red-700": mostRecent.value < 0,
                  "bg-gray-400": mostRecent.value === 0,
                })}
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex w-full items-center justify-between">
          <div className="flex w-min rounded-md bg-orange-50 px-2 py-1">
            <span className="flex text-[9px] font-bold uppercase text-orange-500">
              {isUSALocale ? "Description" : "Descrição"}
            </span>
          </div>

          <GraduationCapIcon className="h-5 w-5 text-gray-300" />
        </div>

        <ExpandableParagraph>
          <SheetDescription>{mostRecent.verbiage.description}</SheetDescription>
        </ExpandableParagraph>
      </div>

      <Separator className="bg-gray-100" />

      {selfie && selfie.isPremium && valuationData.nodes.length >= 2 && (
        <>
          <div className="flex flex-col items-center justify-between sm:flex-row">
            <div className="flex w-full flex-col items-center gap-4 md:flex-row">
              <div className="flex w-full items-center gap-4 md:w-auto">
                {verifyIndicatorIsValuationRatio(indicator) && (
                  <ValuationHistoryFilters
                    filterPeriod={filterPeriod}
                    setFilterPeriod={setFilterPeriod}
                    isUSALocale={isUSALocale}
                  />
                )}
              </div>

              <div className="flex w-full items-center gap-4 md:w-auto">
                <AreaBarPlotTypeSelector value={plotType} onChange={setPlotType} />
                <LogLinearScaleSelector value={axisScaleType} onChange={setAxisScaleType} />
              </div>

              {issuer.__typename === "AmericanCompanyIssuer" && (
                <IssuersSelect
                  title="Search for companies"
                  description="Type a symbol to perform a quick search..."
                  inputSearchPlaceholder="For example: AAPL or NVDA"
                  inputSearchNoResultsText="No results"
                  issuerId={issuer._id}
                  listingCountry={
                    issuer.__typename === "AmericanCompanyIssuer" ? IIssuerListingCountry.Us : IIssuerListingCountry.Br
                  }
                >
                  <Button
                    variant="outline"
                    className={classNames(
                      "w-full text-slate-600 hover:bg-slate-100 sm:w-auto",
                      "disabled:pointer-events-auto disabled:cursor-not-allowed",
                    )}
                    disabled={!(selfie && selfie.isPremium)}
                  >
                    {selectedIssuers.length > 0 ? `Compare (${selectedIssuers.length})` : "Compare"}
                  </Button>
                </IssuersSelect>
              )}
            </div>

            <div className="hidden items-center space-x-2 sm:inline-flex">
              <SimpleTextTooltip content={issuer.name}>
                <Avatar
                  src={issuer.__typename !== "BrazilianRealEstateFund" && issuer.niceLogo ? issuer.niceLogo.url : null}
                  hashValue={issuer._id}
                  alt={issuer.name}
                  size={40}
                  squircle={true}
                />
              </SimpleTextTooltip>

              <SelectedIssuersStack />
            </div>
          </div>

          <div>
            <HighchartsChart
              className="h-56 w-full"
              options={getOptionsForChartHistory(
                valuationIndicatorHistoryForTickerSymbols,
                plotType,
                axisScaleType,
                isAnnual,
                indicator,
                intl,
              )}
            />
          </div>
        </>
      )}

      {selfie && selfie.isPremium && valuationData.nodes.length < 2 && (
        <div className="flex w-full items-center justify-center p-4 font-aeonik">
          <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
            <div>
              <SadFaceIcon />
            </div>

            {isUSALocale ? <p>{`Couldn't find any data.`}</p> : <p>Histórico indisponível.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

interface ValuationHistoryProps {
  filterPeriod: IValuationPeriod;
  setFilterPeriod: (period: IValuationPeriod) => void;
  isUSALocale: boolean;
}

function ValuationHistoryFilters({ filterPeriod, setFilterPeriod, isUSALocale }: ValuationHistoryProps) {
  return (
    <>
      <button
        type="button"
        onClick={() => setFilterPeriod(IValuationPeriod.YearEnd_10Fiscalyear)}
        className={classNames(
          "w-full rounded-full border border-gray-500 bg-gray-50 px-3 py-2 text-[10px] text-xs font-bold text-gray-500 md:w-32",
          {
            "border border-blue-600 bg-blue-50 text-blue-600": filterPeriod === IValuationPeriod.YearEnd_10Fiscalyear,
          },
        )}
      >
        {isUSALocale ? "Annual" : "Anual"}
      </button>
      <button
        type="button"
        onClick={() => setFilterPeriod(IValuationPeriod.MonthEnd_1Ytd)}
        className={classNames(
          "w-full rounded-full border border-gray-500 bg-gray-50 px-3 py-2 text-[10px] text-xs font-bold text-gray-500 md:w-32",
          {
            "border border-blue-600 bg-blue-50 text-blue-600": filterPeriod === IValuationPeriod.MonthEnd_1Ytd,
          },
        )}
      >
        {isUSALocale ? "Monthly" : "Mensal"}
      </button>
    </>
  );
}

function getOptionsForChartHistory(
  valuationIssuers: IValuationIndicatorHistoryForTickerSymbolsQuery["valuationIndicatorHistoryForTickerSymbols"],
  plotType: "area" | "column",
  axisScaleType: AxisScaleType,
  isAnnual: boolean,
  indicator: IValuationIndicator,
  intl: IntlShape,
): Highcharts.Options {
  const series: (SeriesAreaOptions | SeriesLineOptions | SeriesColumnOptions)[] = valuationIssuers.map(
    (series, index) => {
      const theme = getColorThemeForIndex(index);

      if (!theme) {
        throw new Error("Theme not found.");
      }

      const defaultOptions: SeriesAreaOptions | SeriesColumnOptions = {
        showInNavigator: true,
        type: plotType,
        animation: false,
        name: series.tickerSymbol,
        color: theme ? theme.color : undefined,
        lineColor: theme ? theme.lineColor : undefined,
        lineWidth: 2,
        data: series.nodes
          .map((node) => {
            return {
              x: isAnnual ? getYear(parseISO(node.asOfDate)) : parseISO(node.asOfDate).getTime(),
              y: node.value,
              custom: node,
            };
          })
          .sort((a, b) => compareAsc(a.x, b.x)),
        custom: {
          tickerSymbol: series.tickerSymbol,
          primaryColor: theme.color,
          secondaryColor: theme.lineColor,
        },
        marker: {
          width: 8,
          height: 8,
          lineWidth: 1,
          fillColor: theme.color,
          color: theme.color,
          lineColor: theme.lineColor,
          symbol: "circle",
        },
      };

      if (plotType === "column") {
        const columnOptions = {
          borderColor: theme ? theme.lineColor : undefined,
          borderWidth: 1.5,
          states: {
            hover: {
              color: theme ? theme.lineColor : undefined,
            },
          },
        };
        return { ...columnOptions, ...defaultOptions };
      }

      return defaultOptions;
    },
  );
  const minValues = valuationIssuers.map((series) => Math.min(...series.nodes.map((node) => node.value)));
  const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;

  return {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        formatter() {
          if (isAnnual) {
            return String(this.value);
          }
          return intl.formatDate(new Date(this.value), {
            year: "2-digit",
            month: "numeric",
            day: "numeric",
          });
        },
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      title: {
        text: "",
      },
      type: axisScaleType,
      min: minValue !== 0 ? minValue : -1,
      gridLineColor: colors.slate[100],
      labels: {
        align: "center",
        enabled: true,
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter() {
          return valuationIndicatorFormatValue(indicator, parseFloat(String(this.value)));
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter() {
        if (valuationIssuers.length === 0 || !this.points || this.points.length === 0) {
          return "";
        }

        const valuationValues = valuationIssuers[0].nodes;

        const node = (this.point as any).custom as (typeof valuationValues)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "ValuationIndicatorValue") {
          return "";
        }

        return renderToStaticMarkup(
          <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
            <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600">
              {isAnnual
                ? String(getYear(new Date(node.asOfDate)))
                : intl.formatDate(parseISO(node.asOfDate), {
                    year: "2-digit",
                    month: "numeric",
                    day: "numeric",
                  })}
            </span>

            <div className="space-y-1">
              {this.points
                .sort((a, b) => {
                  return (b.y || 0) - (a.y || 0);
                })
                .map(({ key, series, point }) => {
                  const node = (point as any).custom as (typeof valuationValues)[number] | null | undefined;
                  if (!key || !node || node.__typename !== "ValuationIndicatorValue") {
                    return "";
                  }

                  const color = String((series.userOptions as SeriesAreaOptions).color);
                  const lineColor = String((series.userOptions as SeriesAreaOptions).lineColor);
                  return (
                    <div key={color} className="flex items-center justify-between space-x-3">
                      <span
                        className="rounded-md px-2 py-1  font-mono text-xs font-bold"
                        style={{
                          backgroundColor: color,
                          color: lineColor,
                        }}
                      >
                        {series.name}
                      </span>

                      <span className="font-mono font-semibold">{node.formattedValue}</span>
                    </div>
                  );
                })}
            </div>
          </div>,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: colors.slate[500],
            color: colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      area: {
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
        threshold: null,
      },
      column: {
        borderWidth: 1.5,
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };
}

function verifyIsAnnual(period: IValuationPeriod, indicator: IValuationIndicator) {
  if (indicator === IValuationIndicator.PriceToFundsFromOperationsPerShare) {
    return false;
  }

  if (period === IValuationPeriod.YearEnd_10Fiscalyear || !verifyIndicatorIsValuationRatio(indicator)) {
    return true;
  }

  return false;
}
